import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Building2, Palette, Users, CheckCircle, ArrowRight, 
  ArrowLeft, SkipForward, Upload, Loader2, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

const CompanySetupWizard = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { company, refreshCompany } = useSubdomain();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  
  // Step 1: Company Branding
  const [companyName, setCompanyName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Step 2: Theme Colors
  const [primaryColor, setPrimaryColor] = useState('#2B4B6A');
  const [accentColor, setAccentColor] = useState('#F58A5A');
  
  // Step 3: Invite Team
  const [teamEmails, setTeamEmails] = useState('');
  
  const steps = [
    { id: 1, title: 'Company Branding', icon: Building2, description: 'Set your company name and logo' },
    { id: 2, title: 'Theme Colors', icon: Palette, description: 'Customize your brand colors' },
    { id: 3, title: 'Invite Team', icon: Users, description: 'Invite your team members' },
  ];

  // Check company approval status
  const isPendingApproval = company?.approval_status !== 'approved';

  useEffect(() => {
    if (company) {
      setCompanyName(company.name || '');
      if (company.logo_url) {
        setLogoPreview(getLogoUrl(company.logo_url));
      }
      if (company.theme_settings) {
        setPrimaryColor(company.theme_settings.primary_color || '#2B4B6A');
        setAccentColor(company.theme_settings.accent_color || '#F58A5A');
      }
    }
  }, [company]);

  const getLogoUrl = (url) => {
    if (!url) return null;
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    return resolveFileUrl(url, API_URL);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Logo must be less than 5MB');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveStep = async (stepId) => {
    setLoading(true);
    try {
      switch (stepId) {
        case 1:
          // Save company name and logo
          const formData = new FormData();
          formData.append('name', companyName);
          if (logoFile) {
            formData.append('logo', logoFile);
          }
          await api.put(`/companies/${company.id}/branding`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Branding saved!');
          break;
          
        case 2:
          // Save theme colors
          await api.put(`/companies/${company.id}/theme`, {
            primary_color: primaryColor,
            accent_color: accentColor
          });
          toast.success('Theme saved!');
          break;
          
        case 3:
          // Invite team members
          if (teamEmails.trim()) {
            const emails = teamEmails.split(/[,;\n]+/).map(e => e.trim()).filter(e => e && e.includes('@'));
            if (emails.length > 0) {
              await api.post('/invites/send', { emails });
              toast.success(`${emails.length} invite(s) sent!`);
            }
          }
          break;
      }
      
      setCompletedSteps([...completedSteps, stepId]);
      
      if (stepId < 3) {
        setCurrentStep(stepId + 1);
      } else {
        handleComplete();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    try {
      // Mark setup as complete
      await api.put('/users/me', { has_completed_setup: true });
      updateUser({ ...user, has_completed_setup: true });
      toast.success('Setup complete!');
      navigate('/admin');
    } catch (error) {
      console.error('Error completing setup:', error);
      navigate('/admin');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Your Company Name"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="company-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
              <div className="flex items-center gap-4">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-20 h-20 object-contain border rounded-xl" />
                ) : (
                  <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl cursor-pointer hover:bg-gray-200">
                  <Upload className="w-4 h-4" />
                  Upload Logo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">Recommended: 200x200px, max 5MB</p>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-12 h-12 rounded-lg border border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl font-mono"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-12 h-12 rounded-lg border border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl font-mono"
                />
              </div>
            </div>
            
            {/* Preview */}
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600 mb-3">Preview:</p>
              <div className="flex items-center gap-3">
                <button 
                  className="px-4 py-2 rounded-lg text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  Primary Button
                </button>
                <button 
                  className="px-4 py-2 rounded-lg text-white"
                  style={{ backgroundColor: accentColor }}
                >
                  Accent Button
                </button>
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team Member Emails
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Enter email addresses separated by commas or new lines to invite your team.
              </p>
              <textarea
                value={teamEmails}
                onChange={(e) => setTeamEmails(e.target.value)}
                placeholder="colleague@example.com, team@example.com"
                className="w-full h-32 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                data-testid="team-emails-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {teamEmails.split(/[,;\n]+/).filter(e => e.trim() && e.includes('@')).length} email(s) detected
              </p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Trados!</h1>
          <p className="text-gray-500">Let's set up your company in just a few steps</p>
        </div>

        {/* Pending Approval Banner */}
        {isPendingApproval && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-amber-800">Company Pending Approval</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Your company is awaiting approval. You can complete the setup now, but you won't be able to receive quotes until your company is approved.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="mb-8 flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = completedSteps.includes(step.id);
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div 
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                      isCompleted 
                        ? 'bg-green-500 text-white' 
                        : isActive 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? <CheckCircle className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                  </div>
                  <span className={`text-sm font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 ${
                    completedSteps.includes(step.id) ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {steps[currentStep - 1].title}
            </h2>
            <p className="text-gray-500 text-sm">
              {steps[currentStep - 1].description}
            </p>
          </div>

          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleSkip}
                className="flex items-center gap-2 px-4 py-2 text-gray-500 hover:text-gray-700"
              >
                <SkipForward className="w-4 h-4" />
                Skip
              </button>
              
              <button
                onClick={() => handleSaveStep(currentStep)}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50"
                data-testid="save-step-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : currentStep === 3 ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Complete Setup
                  </>
                ) : (
                  <>
                    Save & Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Skip All */}
        <div className="mt-6 text-center">
          <button
            onClick={handleComplete}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Skip setup and go to dashboard →
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanySetupWizard;
